import React, { useEffect, useMemo, useRef, useState } from 'react';

// 급여 기준값 연혁을 가로축 = 시간으로 그린다.
// 묶음의 칸(요율·상한·하한…)마다 한 줄이고, 값이 같은 동안은 한 덩어리로 이어진다. 덩어리의 왼쪽 끝이 값이 바뀐 시행월이다.
// 덩어리를 누르면 아래 표에서 그 시행분이 열린다.

const SIMPLE_TAX = 'simple_tax';
const LABEL_W = 176;
const ZOOMS = [{ id: 'wide', label: '넓게', yearW: 190 }, { id: 'normal', label: '보통', yearW: 110 }, { id: 'tight', label: '촘촘', yearW: 56 }];

const toPercent = (v) => String(Math.round(Number(v) * 1e8) / 1e6);
const formatValue = (unit, value) => (value == null ? '값 없음' : unit === 'percent' ? `${toPercent(value)}%` : Number(value).toLocaleString('ko-KR'));
const parseMonth = (text) => { const [y, m] = text.split('-').map(Number); return y + (m - 1) / 12; };
const prevMonth = (text) => { const [y, m] = text.split('-').map(Number); return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`; };

// 한 줄(묶음의 한 칸)의 덩어리들: 값이 바뀔 때마다 새 덩어리
const buildSegments = (group, field) => {
  const entries = [...group.entries].sort((a, b) => a.effective_from.localeCompare(b.effective_from));
  const isTable = group.item === SIMPLE_TAX;
  const segments = [];
  entries.forEach((entry) => {
    const value = isTable ? entry.value : entry.value ? entry.value[field.field] : null;
    const text = isTable ? (value == null ? '값 없음' : `${value.row_count}구간`) : formatValue(field.unit, value);
    const last = segments[segments.length - 1];
    if (last && !isTable && last.text === text) return;    // 이 시행월에는 다른 칸이 바뀌었다
    if (last) last.end = entry.effective_from;
    segments.push({ start: entry.effective_from, end: null, text, none: value == null, entryId: entry.id });
  });
  return segments;
};

function RatesTimelineChart({ groups, month, selected, onPick }) {
  const [zoom, setZoom] = useState('normal');
  const yearW = ZOOMS.find((z) => z.id === zoom).yearW;
  const scrollRef = useRef(null);

  const { lanes, startYear, endYear } = useMemo(() => {
    const out = [];
    groups.forEach((group, gi) => {
      const fields = group.item === SIMPLE_TAX || !group.fields.length ? [{ field: '_', label: '' }] : group.fields;
      fields.forEach((field, fi) => {
        out.push({
          key: `${group.item}/${field.field}`, group, tone: gi % 6, first: fi === 0, count: fields.length,
          title: group.section ? `${group.section} · ${group.label}` : group.label,
          sub: fields.length > 1 ? field.label : '',
          segments: buildSegments(group, field),
        });
      });
    });
    const starts = out.flatMap((l) => l.segments.map((s) => parseMonth(s.start)));
    const now = new Date().getFullYear();
    const base = parseMonth(month);
    return {
      lanes: out,
      startYear: Math.floor(Math.min(base, ...(starts.length ? starts : [now]))),
      endYear: Math.floor(Math.max(base, now, ...(starts.length ? starts : [now]))) + 2,
    };
  }, [groups, month]);

  const x = (t) => (t - startYear) * yearW;
  const width = x(endYear);
  const years = Array.from({ length: endYear - startYear }, (_, i) => startYear + i);
  const monthX = x(parseMonth(month) + 1 / 24);      // 기준 월의 가운데

  // 기준 월이 화면 오른쪽 3분의 1쯤에 오게 옮긴다
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = Math.max(0, monthX - (el.clientWidth - LABEL_W) * 0.66);
  }, [monthX, yearW]);

  return (
    <div className="prm-chart">
      <div className="prm-chart-head">
        <b>연혁 한눈에 보기</b>
        <span className="prm-muted">가로축이 시간입니다. 값이 같은 동안은 한 덩어리이고, 덩어리가 바뀌는 곳이 시행월입니다. 덩어리를 누르면 아래에서 그 시행분이 열립니다.</span>
        <span className="prm-chart-zoom">
          {ZOOMS.map((z) => (
            <button type="button" key={z.id} className={zoom === z.id ? 'on' : ''} onClick={() => setZoom(z.id)}>{z.label}</button>
          ))}
        </span>
      </div>
      <div className="prm-chart-scroll" ref={scrollRef}>
        <div className="prm-chart-body" style={{ width: LABEL_W + width }}>
          <div className="prm-chart-row axis">
            <div className="prm-chart-label" style={{ width: LABEL_W }} />
            <div className="prm-chart-track" style={{ width }}>
              {years.map((y) => <span key={y} className="prm-chart-year" style={{ left: x(y), width: yearW }}>{y}</span>)}
              <span className="prm-chart-now-tag" style={{ left: monthX }}>{month}</span>
            </div>
          </div>
          {lanes.map((lane) => {
            const firstStart = lane.segments.length ? parseMonth(lane.segments[0].start) : endYear;
            return (
              <div className={`prm-chart-row tone-${lane.tone} ${lane.first ? 'first' : ''}`} key={lane.key}>
                <div className="prm-chart-label" style={{ width: LABEL_W }}>
                  {lane.first && <b>{lane.title}</b>}
                  {lane.sub && <span>{lane.sub}</span>}
                </div>
                <div className="prm-chart-track" style={{ width, backgroundSize: `${yearW}px 100%` }}>
                  {firstStart > startYear && (
                    <span
                      className={`prm-chart-seg gap ${lane.group.optional || lane.group.section ? 'optional' : ''}`}
                      style={{ left: 0, width: x(firstStart) }}
                      title={`${lane.title}: 이 기간에는 등록된 이력이 없습니다(미등록)`}
                    >
                      미등록
                    </span>
                  )}
                  {lane.segments.map((s, i) => {
                    const left = x(parseMonth(s.start));
                    const right = s.end ? x(parseMonth(s.end)) : width;
                    const until = s.end ? prevMonth(s.end) : '지금';
                    return (
                      <button
                        type="button"
                        key={s.start}
                        className={`prm-chart-seg ${i % 2 ? 'alt' : ''} ${s.none ? 'none' : ''} ${selected === s.entryId ? 'on' : ''}`}
                        style={{ left, width: right - left }}
                        title={`${lane.title}${lane.sub ? ` · ${lane.sub}` : ''}: ${s.text}\n${s.start} ~ ${until}\n누르면 ${s.start} 시행분이 열립니다`}
                        onClick={() => onPick(lane.group, s.entryId)}
                      >
                        <span>{s.text}</span>
                      </button>
                    );
                  })}
                  <i className="prm-chart-now" style={{ left: monthX }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default RatesTimelineChart;
